import { Spacing } from '@persona-id/design-tokens';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import { Blueprint, BlueprintBlockType } from 'dashboard/models/blueprint';
import { Case } from 'dashboard/models/case';
import {
  DashboardPageLink,
  GuideStepTipBanner,
  UnorderedList,
} from 'dashboard/views/DashboardSandboxHomeView/constants';
import Button from 'design-system/components/Button';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { DATE_AND_TIME_TZ_FORMAT } from 'lib/constants/luxonFormats';

interface ReviewCaseStepProps {
  casesForSelectedBlueprint: Case[];
  isLoadingCasesForSelectedBlueprint: boolean;
  selectedBlueprint: Blueprint;
  isStepCompleted: boolean;
  isSavingCompletedStep: boolean;
  handleLoadCasesForSelectedBlueprint: (caseTemplateIds: string[]) => Promise<void>;
  saveCompletedStep: () => void;
}

const ReviewCaseStep = ({
  casesForSelectedBlueprint,
  isLoadingCasesForSelectedBlueprint,
  selectedBlueprint,
  isStepCompleted,
  isSavingCompletedStep,
  handleLoadCasesForSelectedBlueprint,
  saveCompletedStep,
}: ReviewCaseStepProps) => {
  const [selectedCase, setSelectedCase] = useState(null);

  const handleCaseChange = (value: string) => {
    const kase = casesForSelectedBlueprint.filter((kase) => kase.id === value)[0];
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setSelectedCase(kase);
  };

  return (
    <>
      <Paragraph color='primary'>
        Cases is an identity investigation center that enables you and your team to build a more
        robust and efficient review process. In Cases, you have a single place to track,
        investigate, and decide on situations you want to manually review. It can pull together
        information from an Inquiry, Verifications, Reports, and more.
      </Paragraph>
      <Spacer height={2} />
      <Paragraph color='primary'>
        Cases is often used with Workflows. Your solution uses this combination. The Workflow
        creates a Case when the Inquiry meets certain criteria — usually criteria that indicate the
        Inquiry is non-standard or more risky.
      </Paragraph>
      <Spacer height={2} />
      <Paragraph color='primary'>
        Let’s take a look at the Case that the Workflow just created.
      </Paragraph>
      <Spacer height={2} />
      <Paragraph color='primary'>
        Note: If you don’t see a Case below, create another test Inquiry. When you go through this
        Inquiry, set the toggle to “Fail verifications.” (Depending on your solution, doing this may
        still not create a Case. If that happens, feel free to skip this step for now.)
      </Paragraph>
      <Spacer height={1} />
      <Select
        aria-label='Select a Case'
        disabled={isLoadingCasesForSelectedBlueprint || casesForSelectedBlueprint.length === 0}
        placeholder={
          casesForSelectedBlueprint.length === 0
            ? 'No Cases for this solution yet!'
            : 'Choose a Case...'
        }
        onValueChange={(value) => handleCaseChange(value)}
      >
        {casesForSelectedBlueprint.map((kase) => (
          <Option key={`option-${kase.id}`} value={kase.id}>
            Case created on{' '}
            {DateTime.fromISO(kase.attributes['created-at']).toFormat(DATE_AND_TIME_TZ_FORMAT)} (
            {kase.id})
          </Option>
        ))}
      </Select>
      <Spacer height={1} />
      <ButtonRow>
        {isSavingCompletedStep ? (
          <Button disabled width='button'>
            Saving...
          </Button>
        ) : (
          <Button
            id='home-sandbox-onboarding-checklist__view-case'
            disabled={!selectedCase || isLoadingCasesForSelectedBlueprint}
            width='button'
            onClick={() => {
              if (!isStepCompleted) {
                saveCompletedStep();
              }
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              window.open(DashboardPageLink.Case.replace('[case-token]', selectedCase.id));
            }}
          >
            View Case
          </Button>
        )}
        <Button
          id='home-sandbox-onboarding-checklist__refresh-cases-list'
          disabled={isLoadingCasesForSelectedBlueprint}
          variant='basic'
          width='button'
          onClick={() => {
            const caseTemplateBlocks = selectedBlueprint.attributes.blocks.filter(
              (block) => block.type === BlueprintBlockType.CaseTemplate,
            );
            if (caseTemplateBlocks.length > 0) {
              handleLoadCasesForSelectedBlueprint(caseTemplateBlocks.map((ctmpl) => ctmpl.token));
            }
          }}
        >
          Refresh Cases
        </Button>
      </ButtonRow>
      <Spacer height={3} />
      <GuideStepTipBanner>
        <Paragraph color='primary'>
          Tip: By default, all user roles can view all Cases for the Case Template that is part of
          your solution. You can change this permission on the{' '}
          <Link to='/dashboard/user-roles' target='_blank'>
            Roles page
          </Link>
          . See our Help Center to learn more about Cases:
        </Paragraph>
        <UnorderedList>
          <li>
            <a
              href='https://support.withpersona.com/hc/en-us/articles/360058687794-Cases-overview'
              target='_blank'
              rel='noreferrer'
            >
              Cases overview
            </a>
          </li>
        </UnorderedList>
      </GuideStepTipBanner>
    </>
  );
};

const ButtonRow = styled('div')`
  display: flex;
  flex-direction: row;
  column-gap: ${Spacing[2]};
`;

export default ReviewCaseStep;
